import React, { useState, useEffect, useRef, Suspense, lazy, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { scrollToSection } from "../utils/scrollUtils";
import ScrollProgress from "./ScrollProgress";
import "../styles/Navbar.css";

import HeroPage from "../pages/HeroSection";

const About        = lazy(() => import("../pages/About"));
const Education    = lazy(() => import("../pages/Education"));
const Experience   = lazy(() => import("../pages/Experience"));
const Skills       = lazy(() => import("../pages/Skills"));
const Certifications = lazy(() => import("../pages/Certifications"));
const Projects     = lazy(() => import("../pages/Projects"));
const Blog         = lazy(() => import("../pages/Blog"));
const Contact      = lazy(() => import("../pages/Contact"));

const sections = [
  { name: "About",          id: "About" },
  { name: "Education",      id: "Education" },
  { name: "Experience",     id: "Experience" },
  { name: "Skills",         id: "Skills" },
  { name: "Certifications", id: "Certifications" },
  { name: "Projects",       id: "Projects" },
  { name: "Blog",           id: "Blog" },
  { name: "Contact",        id: "Contact" },
];

const Layout: React.FC = () => {
  const [activeSection,    setActiveSection]    = useState("Hero");
  const [menuOpen,         setMenuOpen]         = useState(false);
  const [pastHero,         setPastHero]         = useState(false);
  const [isMobile,         setIsMobile]         = useState(false);

  const heroRef = useRef<HTMLDivElement>(null);

  /* ── detect mobile ── */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ── active-section tracking via IntersectionObserver (no scroll listeners) ── */
  useEffect(() => {
    const targets = sections
      .map(s => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);

    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );

    targets.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* ── nav solidify + scroll-to-top visibility, driven by Hero's own visibility ── */
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setPastHero(!entry.isIntersecting);
        if (entry.isIntersecting) setActiveSection("Hero");
      },
      { threshold: 0 }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  /* ── close menu on wide resize ── */
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMenuOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── lock body scroll when overlay open ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [menuOpen]);

  /* ── navigation helper ── */
  const handleNav = useCallback((id: string) => {
    if (menuOpen) {
      setMenuOpen(false);
      setTimeout(() => {
        scrollToSection(id, isMobile ? 88 : 64);
        setActiveSection(id);
      }, 300);
    } else {
      scrollToSection(id, isMobile ? 88 : 64);
      setActiveSection(id);
    }
  }, [menuOpen, isMobile]);

  /* ── keyboard navigation ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && menuOpen) { setMenuOpen(false); return; }
      if (menuOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        const idx = sections.findIndex(s => s.id === activeSection);
        if (e.key === "ArrowDown" && idx < sections.length - 1) handleNav(sections[idx + 1].id);
        if (e.key === "ArrowUp"   && idx > 0)                   handleNav(sections[idx - 1].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, activeSection, handleNav]);

  return (
    <div className="relative min-h-screen bg-paper">
      <ScrollProgress />

      {/* ═══════════════ NAV ROOT ═══════════════ */}
      <header
        className={`nav-root${pastHero ? " nav-root--scrolled" : ""}`}
        role="banner"
      >
        <div className="nav-inner">

          {/* ── Logo ── */}
          <button
            className="nav-logo-btn"
            onClick={() => handleNav("Hero")}
            aria-label="Go to top"
          >
            <span className="nav-logo-name">Jaisheel Polimera</span>
          </button>

          {/* ── Desktop links ── */}
          <nav className="nav-links" aria-label="Main navigation">
            {sections.map((s) => (
              <button
                key={s.id}
                className={`nav-link${activeSection === s.id ? " nav-link--active" : ""}`}
                onClick={() => handleNav(s.id)}
                aria-current={activeSection === s.id ? "page" : undefined}
              >
                {s.name}
              </button>
            ))}
          </nav>

          {/* ── Hamburger (mobile) ── */}
          <button
            className={`nav-burger${menuOpen ? " nav-burger--open" : ""}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            type="button"
          >
            <span className="nav-burger-bar" />
            <span className="nav-burger-bar" />
            <span className="nav-burger-bar" />
          </button>

        </div>
      </header>

      {/* ═══════════════ MOBILE OVERLAY ═══════════════ */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            role="navigation"
            aria-label="Mobile menu"
          >
            <ul className="nav-overlay-list" role="list">
              {sections.map((s, i) => (
                <motion.li
                  key={s.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <button
                    className={`nav-mobile-link${activeSection === s.id ? " nav-mobile-link--active" : ""}`}
                    onClick={() => handleNav(s.id)}
                    aria-current={activeSection === s.id ? "page" : undefined}
                    type="button"
                  >
                    <span className="nav-mobile-name">{s.name}</span>
                    <span className="nav-mobile-arrow">→</span>
                  </button>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════ HERO ═══════════════ */}
      <section ref={heroRef} id="Hero" className="relative min-h-[100dvh]" aria-label="Hero section">
        <HeroPage />
      </section>

      {/* ═══════════════ PAGE SECTIONS ═══════════════ */}
      <main role="main">
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="spinner" aria-label="Loading" />
            </div>
          }
        >
          <Section id="About">        <About />          </Section>
          <Section id="Education">    <Education />      </Section>
          <Section id="Experience">   <Experience />     </Section>
          <Section id="Skills">       <Skills />         </Section>
          <Section id="Certifications"><Certifications /></Section>
          <Section id="Projects">     <Projects />       </Section>
          <Section id="Blog">         <Blog />           </Section>
          <Section id="Contact">      <Contact />        </Section>
        </Suspense>
      </main>

      {/* ═══════════════ SCROLL-TO-TOP ═══════════════ */}
      <AnimatePresence>
        {pastHero && (
          <motion.button
            className="nav-scroll-top"
            onClick={() => handleNav("Hero")}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            aria-label="Scroll to top"
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

const Section: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <section
    id={id}
    className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 md:px-8"
    aria-labelledby={`${id}-title`}
  >
    {children}
  </section>
);

export default Layout;
